import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import type { CacheMode, DemoTypes } from "@/constants";
import { EnerfloProposalData, OptimizeDesignOperationType, PricingOperation, ProposalPdfInfo, SelectOption } from "../../types";
import { OptimizeDesignAction } from "../../types/design-optimizer";
import { CalculatePricingAction } from "../../types/pricing-calculator";
import { syncAuroraV2Design } from "../auroraV2/aurora-V2.actions";
import { syncSoloProposal } from "../solo/solo.actions";
import {
  applyForFinancing,
  fetchProposalData,
  fetchProposalPdfInfo,
  fetchRateScheduleOptions,
  recalculatePricing,
  refreshDesignData,
  submitOptimization,
  syncDesignData,
} from "./proposal.actions";

type RateSchedulesState = {
  loading?: boolean;
  error?: {
    statusCode?: number;
    message: string;
  };
  options?: SelectOption[];
};

type ProposalState = {
  reportHash: string;
  demoType?: DemoTypes;
  cacheMode?: CacheMode;
  data?: EnerfloProposalData;
  isLoading: boolean;
  isRecalculatingPrice?: boolean;
  activePricingOperation?: PricingOperation;
  isOptimizingDesign?: boolean;
  applyingForFinancing?: boolean;
  activeDesignOptimization?: OptimizeDesignOperationType;
  pdfInfo?: ProposalPdfInfo;
  error?: {
    statusCode: number;
    message: string;
  };
  optimizeError?: {
    statusCode?: string;
    message?: string;
  };
  recalculatePricingError?: {
    statusCode?: string;
    message?: string;
    details?: any;
  };
  locked: boolean;
  rateSchedules: RateSchedulesState;
};

const initialState = {
  isLoading: false,
  locked: true,
  rateSchedules: {},
} as ProposalState;

const proposalSlice = createSlice({
  name: "proposal",
  initialState,
  reducers: {
    init(state, action: PayloadAction<{ id: string; demoType?: DemoTypes; cacheMode?: CacheMode }>) {
      state.reportHash = action.payload.id;

      if (action.payload.demoType) {
        state.demoType = action.payload.demoType;
      }

      if (action.payload.cacheMode) {
        state.cacheMode = action.payload.cacheMode;
      }
    },
    initPreview(state, action: PayloadAction<{ id: string; demoType?: DemoTypes }>) {
      state.reportHash = action.payload.id;

      if (action.payload.demoType) {
        state.demoType = action.payload.demoType;
      }
    },
    unlock(state) {
      state.locked = false;
    },
    toggleLock(state) {
      state.locked = !state.locked;
    },
    modifyDesign(state, action: PayloadAction<any>) {
      if (state.data && action.payload) {
        state.data.design = {
          ...state.data.design,
          ...action.payload,
        };
      }
    },
    startRecalculatePricingSaga(state, action: PayloadAction<CalculatePricingAction>) {},
    startSubmitOptimizationSaga(state, action: PayloadAction<OptimizeDesignAction>) {},
  },
  extraReducers: builder => {
    builder
      .addCase(submitOptimization.pending, (state, action) => {
        state.optimizeError = undefined;
        state.isOptimizingDesign = true;
        state.activeDesignOptimization = action.meta.arg.type;
      })
      .addCase(submitOptimization.fulfilled, (state, action) => {
        state.isOptimizingDesign = false;
        state.activeDesignOptimization = undefined;
        state.data = action.payload;
      })
      .addCase(submitOptimization.rejected, (state, action) => {
        state.optimizeError = getError(action);
        state.isOptimizingDesign = false;
        state.activeDesignOptimization = undefined;
      });

    builder
      .addCase(recalculatePricing.pending, (state, action) => {
        state.recalculatePricingError = undefined;
        state.isRecalculatingPrice = true;
        state.activePricingOperation = action.meta.arg.type;
      })
      .addCase(recalculatePricing.fulfilled, (state, action) => {
        state.isRecalculatingPrice = false;
        state.activePricingOperation = undefined;
        state.data = action.payload;
      })
      .addCase(recalculatePricing.rejected, (state, action) => {
        state.recalculatePricingError = getError(action);
        state.isRecalculatingPrice = false;
        state.activePricingOperation = undefined;
      });

    builder
      .addCase(fetchRateScheduleOptions.pending, (state, action) => {
        state.rateSchedules.loading = true;
        delete state.rateSchedules.error;
      })
      .addCase(fetchRateScheduleOptions.fulfilled, (state, action) => {
        state.rateSchedules.loading = false;
        state.rateSchedules.options = action.payload;
      })
      .addCase(fetchRateScheduleOptions.rejected, (state, action) => {
        state.rateSchedules.loading = false;
        state.rateSchedules.error = {
          statusCode: action.payload?.statusCode,
          message: action.payload?.message ?? "Something went wrong",
        };
      });

    builder
      .addCase(fetchProposalData.pending, (state, action) => {
        state.isLoading = true;
        state.pdfInfo = undefined;
      })
      .addCase(fetchProposalData.fulfilled, (state, action) => {
        state.data = action.payload;
        state.isLoading = false;
        state.error = undefined;
      })
      .addCase(fetchProposalData.rejected, (state, action) => {
        state.isLoading = false;

        if (action.meta.rejectedWithValue) {
          state.error = action.payload as any;
        }
      });

    builder.addCase(fetchProposalPdfInfo.fulfilled, (state, action) => {
      state.pdfInfo = action.payload;
    });

    builder
      .addCase(refreshDesignData.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(refreshDesignData.fulfilled, (state, action) => {
        state.data = action.payload;
        state.isLoading = false;
      })
      .addCase(refreshDesignData.rejected, (state, action) => {
        state.isLoading = false;
      });

    builder
      .addCase(applyForFinancing.pending, (state, action) => {
        state.applyingForFinancing = true;
      })
      .addCase(applyForFinancing.fulfilled, (state, action) => {
        state.applyingForFinancing = false;
      })
      .addCase(applyForFinancing.rejected, (state, action) => {
        state.applyingForFinancing = false;
      });

    builder
      .addCase(syncSoloProposal.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(syncSoloProposal.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(syncSoloProposal.rejected, (state, action) => {
        state.isLoading = false;
      });

    builder
      .addCase(syncAuroraV2Design.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(syncAuroraV2Design.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(syncAuroraV2Design.rejected, (state, action) => {
        state.isLoading = false;
      });

    builder
      .addCase(syncDesignData.pending, state => {
        state.isLoading = true;
      })
      .addCase(syncDesignData.fulfilled, (state, action) => {
        state.data = action.payload;
        state.isLoading = false;
      })
      .addCase(syncDesignData.rejected, state => {
        state.isLoading = false;
      });
  },
});

export const {
  init: initProposal,
  initPreview,
  toggleLock,
  startRecalculatePricingSaga,
  startSubmitOptimizationSaga,
  modifyDesign,
} = proposalSlice.actions;

export default proposalSlice;

function getError(action) {
  if (action.payload?.["errors"]) {
    return {
      statusCode: action.error?.code,
      message: action.payload?.["errors"],
      details: action.payload?.["details"],
    };
  } else {
    return { statusCode: action.error?.code, message: action.error?.message };
  }
}
